import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Top-level error boundary. Catches render-time exceptions in child tree so
 * the whole app doesn't white-screen. Renders a Gulf-styled retry card.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const ar = document.documentElement.lang === "ar";
    return (
      <div className="min-h-screen mobile-dashboard flex items-center justify-center px-5" dir={ar ? "rtl" : "ltr"} data-testid="error-boundary">
        <div className="max-w-sm w-full rounded-3xl border border-red-500/40 p-6 text-center" style={{ background: "linear-gradient(160deg, rgba(220,38,38,0.15), rgba(0,0,0,0.6))" }}>
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto" />
          <h1 className="text-xl font-black text-white mt-3">
            {ar ? "عذراً، حدث خطأ غير متوقع" : "Something went wrong"}
          </h1>
          <p className="text-[12px] text-white/70 mt-2 leading-relaxed">
            {ar
              ? "حصل خطأ في هذا الجزء من التطبيق. يمكنك المحاولة مرة أخرى أو العودة للرئيسية."
              : "An unexpected error occurred. You can retry or go back to the home screen."}
          </p>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-3 p-2 bg-black/60 border border-red-500/30 rounded-lg text-[9px] text-red-300 text-start overflow-auto max-h-32">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
          <div className="mt-5 flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reset}
              className="px-4 py-2 rounded-full btn-gold-outline text-xs font-bold"
              data-testid="err-retry"
            >
              {ar ? "إعادة المحاولة" : "Retry"}
            </button>
            <button
              type="button"
              onClick={this.goHome}
              className="px-4 py-2 rounded-full btn-gold text-xs font-bold"
              data-testid="err-home"
            >
              {ar ? "الرئيسية" : "Home"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
